/**
 * Fonte de verdade centralizada para o estado do pipeline de agentes AG-UI.
 *
 * Modelo: 4 personas (Archie, Dochie, Jobs, Van Gogh), cada uma com 1+ skills.
 * Dochie tem 4 skills (tech/risk/innovation/sustainability reviewers) que rodam em
 * paralelo; Van Gogh tem 2 skills (diagrams, prototype) que também rodam em
 * paralelo dentro do step umbrella `output.parallel`.
 *
 * Steps no streaming (em ordem):
 *   archie.ingest           — sem LLM
 *   dochie.review           — 4 sub-steps (skill_id) em paralelo
 *   archie.synthesis        — SDD + ADRs com HITL embutido
 *   output.parallel         — umbrella; abriga jobs.event_storm, vangogh.diagrams, vangogh.prototype
 */
import { useMemo } from 'react'
import type { AgentEvent, UserPresence } from '../types'
import {
  effectiveEventKind,
  effectivePipelineStep,
} from '../agui/agUiNormalization'
import type { AgentStepProgress, StepState } from './useAgentProgress'

export type AgentStatus = 'idle' | 'working' | 'done' | 'error'

export interface AgentStepLabel {
  step: string
  label: string
  activeSkills?: string[]
}

export interface AgentProgress {
  steps: AgentStepProgress[]
  activeStepIndex: number
  doneCount: number
  total: number
}

export interface AgentRunState {
  statuses: Record<string, AgentStatus>
  peers: UserPresence[]
  stepLabels: Record<string, AgentStepLabel>
  progress: AgentProgress
  reasoningEvents: AgentEvent[]
  activeStep: string | null
  hasRunMarker: boolean
  isWaitingWarmup: boolean
}

/** Ordem dos steps umbrella para a barra de progresso e label de "estágio atual". */
const PIPELINE_ORDER = [
  'archie.ingest',
  'dochie.review',
  'archie.synthesis',
  'output.parallel',
] as const

/** Sub-steps reportados via skill_id dentro de `output.parallel`. */
const OUTPUT_SUB_STEPS = [
  'jobs.event_storm',
  'vangogh.diagrams',
  'vangogh.prototype',
] as const

/** Mapeamento legível para skills de cada persona (usado em labels). */
const SKILL_LABELS: Record<string, string> = {
  tech_reviewer: 'Técnico',
  risk_reviewer: 'Riscos',
  innovation_reviewer: 'Inovação',
  sustainability_reviewer: 'Sustentabilidade',
  ingest: 'Ingestão',
  synthesis: 'Síntese SDD',
  event_storm: 'Event Storm',
  diagrams: 'Diagramas',
  prototype: 'Protótipo',
}

const AGENT_DEFS = [
  {
    id: 'agent-001',
    personaId: 'archie',
    name: 'Archie (Arquiteto de Soluções)',
    email: 'archie@archforge.local',
    color: '#8b5cf6',
    steps: ['archie.ingest', 'archie.synthesis'],
    activeLabel: (step: string) =>
      step === 'archie.ingest' ? 'Ingerindo contexto' : 'Sintetizando SDD + ADRs (com HITL)',
    doneLabel: 'Arquitetura documentada nesta sala',
    idleLabel: 'Aguardando pipeline…',
  },
  {
    id: 'agent-002',
    personaId: 'dochie',
    name: 'Dochie (Analista Multidimensional)',
    email: 'dochie@archforge.local',
    color: '#10b981',
    steps: ['dochie.review'],
    activeLabel: (_step: string) => 'Análise multidimensional em curso',
    doneLabel: 'Análise concluída nesta sala',
    idleLabel: 'Aguardando pipeline…',
  },
  {
    id: 'agent-003',
    personaId: 'jobs',
    name: 'Jobs (Event Storming)',
    email: 'jobs@archforge.local',
    color: '#f59e0b',
    steps: ['jobs.event_storm'],
    activeLabel: (_step: string) => 'Modelando Event Storm',
    doneLabel: 'Event Storm modelado',
    idleLabel: 'Aguardando síntese arquitetural…',
  },
  {
    id: 'agent-004',
    personaId: 'vangogh',
    name: 'Van Gogh (Visualizador de Arquitetura)',
    email: 'vangogh@archforge.local',
    color: '#f43f5e',
    steps: ['vangogh.diagrams', 'vangogh.prototype'],
    activeLabel: (step: string) =>
      step === 'vangogh.diagrams' ? 'Gerando diagramas Mermaid' : 'Desenhando protótipo visual',
    doneLabel: 'Diagramas e protótipo entregues',
    idleLabel: 'Aguardando síntese arquitetural…',
  },
] as const

/* ---------- helpers puros ---------- */

function sliceEventsAfterLatestRun(events: AgentEvent[]): AgentEvent[] {
  if (!events?.length) return []
  let lastRunIdx = -1
  for (let i = events.length - 1; i >= 0; i--) {
    if (effectiveEventKind(events[i] ?? {}) === 'RUN_STARTED') {
      lastRunIdx = i
      break
    }
  }
  if (lastRunIdx === -1) return events
  return events.slice(lastRunIdx)
}

/** Lê skill_id de metadata.archforge.skill_id quando presente. */
function eventSkillId(ev: AgentEvent | undefined): string | undefined {
  if (!ev) return undefined
  const meta = (ev as AgentEvent & { metadata?: { archforge?: { skill_id?: unknown } } }).metadata
  const sid = meta?.archforge?.skill_id
  return typeof sid === 'string' ? sid : undefined
}

interface PipelineAnalysis {
  started: Set<string>
  finished: Set<string>
  skillsStarted: Set<string>
  skillsFinished: Set<string>
}

function analyzePipelineSteps(events: AgentEvent[]): PipelineAnalysis {
  const started = new Set<string>()
  const finished = new Set<string>()
  const skillsStarted = new Set<string>()
  const skillsFinished = new Set<string>()
  for (const ev of events) {
    const k = effectiveEventKind(ev)
    const step = effectivePipelineStep(ev)
    if (!step) continue
    const sid = eventSkillId(ev)
    if (k === 'STEP_STARTED') {
      if (sid) skillsStarted.add(sid)
      else started.add(step)
    }
    if (k === 'STEP_FINISHED') {
      if (sid) skillsFinished.add(sid)
      else finished.add(step)
    }
  }
  return { started, finished, skillsStarted, skillsFinished }
}

function findActiveStep(events: AgentEvent[]): string | null {
  const { started, finished } = analyzePipelineSteps(sliceEventsAfterLatestRun(events))
  for (const step of PIPELINE_ORDER) {
    if (started.has(step) && !finished.has(step)) return step
  }
  return null
}

function deriveStatuses(
  scoped: AgentEvent[],
  pipelineBusy: boolean,
  hasSdd: boolean,
  hasPrototype: boolean,
  hasDiagrams: boolean,
  isError: boolean,
): Record<string, AgentStatus> {
  if (isError) {
    return { archie: 'error', dochie: 'error', jobs: 'error', vangogh: 'error' }
  }

  const { started, finished, skillsStarted, skillsFinished } = analyzePipelineSteps(scoped)

  // Pré-aquecimento: pipelineBusy mas ainda não chegou RUN_STARTED
  if (pipelineBusy && scoped.length === 0) {
    return { archie: 'working', dochie: 'idle', jobs: 'idle', vangogh: 'idle' }
  }

  const tail = scoped[scoped.length - 1]
  const waitingNextRunWarmup =
    pipelineBusy &&
    scoped.length > 0 &&
    effectiveEventKind(tail ?? {}) === 'RUN_FINISHED'

  if (waitingNextRunWarmup) {
    return { archie: 'working', dochie: 'idle', jobs: 'idle', vangogh: 'idle' }
  }

  const hasRunMarker = scoped.some((e) => effectiveEventKind(e ?? {}) === 'RUN_STARTED')
  const stepsPresent = started.size > 0 || finished.size > 0

  if (pipelineBusy && hasRunMarker && !stepsPresent) {
    return { archie: 'working', dochie: 'idle', jobs: 'idle', vangogh: 'idle' }
  }

  const isStepActive = (step: string) => started.has(step) && !finished.has(step)

  const archieActive = isStepActive('archie.ingest') || isStepActive('archie.synthesis')
  const archieDone = finished.has('archie.synthesis') || finished.has('archie.ingest')

  // Dochie: working enquanto o umbrella step está ativo ou qualquer dos 4 skills ainda não concluiu
  const dochieSkillIds = ['tech_reviewer', 'risk_reviewer', 'innovation_reviewer', 'sustainability_reviewer']
  const dochieActive =
    isStepActive('dochie.review') ||
    dochieSkillIds.some((sid) => skillsStarted.has(sid) && !skillsFinished.has(sid))
  const dochieDone = finished.has('dochie.review')

  // Jobs
  const jobsActive = isStepActive('jobs.event_storm') || (skillsStarted.has('event_storm') && !skillsFinished.has('event_storm'))
  const jobsDone = finished.has('jobs.event_storm') || skillsFinished.has('event_storm')

  // Van Gogh: qualquer dos 2 skills
  const vgActive =
    isStepActive('vangogh.diagrams') ||
    isStepActive('vangogh.prototype') ||
    (skillsStarted.has('diagrams') && !skillsFinished.has('diagrams')) ||
    (skillsStarted.has('prototype') && !skillsFinished.has('prototype'))
  const vgDone =
    (finished.has('vangogh.diagrams') || skillsFinished.has('diagrams')) &&
    (finished.has('vangogh.prototype') || skillsFinished.has('prototype'))

  return {
    archie: archieActive ? 'working' : archieDone || hasSdd ? 'done' : 'idle',
    dochie: dochieActive ? 'working' : dochieDone ? 'done' : 'idle',
    jobs: jobsActive ? 'working' : jobsDone ? 'done' : 'idle',
    vangogh: vgActive ? 'working' : vgDone || hasPrototype || hasDiagrams ? 'done' : 'idle',
  }
}

function derivePeers(
  statuses: Record<string, AgentStatus>,
  started: Set<string>,
  finished: Set<string>,
  skillsStarted: Set<string>,
  skillsFinished: Set<string>,
): UserPresence[] {
  return AGENT_DEFS.map((def) => {
    const status = statuses[def.personaId] || 'idle'

    const activeStep = def.steps.find((s) => started.has(s) && !finished.has(s))
    const doneSteps = def.steps.filter((s) => finished.has(s))

    let badge = ''
    let agentStatus: UserPresence['agentStatus'] = 'idle'

    switch (status) {
      case 'working':
        agentStatus = 'thinking'
        if (def.personaId === 'dochie') {
          const activeReviewers = ['tech_reviewer', 'risk_reviewer', 'innovation_reviewer', 'sustainability_reviewer']
            .filter((sid) => skillsStarted.has(sid) && !skillsFinished.has(sid))
            .map((sid) => SKILL_LABELS[sid] || sid)
          badge = activeReviewers.length > 0
            ? `Analisando: ${activeReviewers.join(', ')}`
            : def.activeLabel(activeStep || def.steps[0])
        } else if (def.personaId === 'vangogh') {
          const activeSkills = (['diagrams', 'prototype'] as const)
            .filter((sid) => skillsStarted.has(sid) && !skillsFinished.has(sid))
            .map((sid) => SKILL_LABELS[sid] || sid)
          badge = activeSkills.length > 0
            ? `Gerando: ${activeSkills.join(', ')}`
            : def.activeLabel(activeStep || def.steps[0])
        } else {
          badge = activeStep ? def.activeLabel(activeStep) : def.idleLabel
        }
        break
      case 'done':
        agentStatus = 'completed'
        badge = doneSteps.length > 0 ? def.doneLabel : def.idleLabel
        break
      case 'error':
        agentStatus = 'error'
        badge = 'Erro na última execução'
        break
      default:
        agentStatus = 'idle'
        badge = def.idleLabel
    }

    return {
      id: def.id,
      name: def.name,
      email: def.email,
      color: def.color,
      cursor: undefined,
      status: status === 'working' || status === 'done' ? 'online' : 'away',
      agentStatus,
      badge,
    }
  })
}

function deriveStepLabels(
  started: Set<string>,
  finished: Set<string>,
  skillsStarted: Set<string>,
  skillsFinished: Set<string>,
): Record<string, AgentStepLabel> {
  const labels: Record<string, AgentStepLabel> = {}
  for (const def of AGENT_DEFS) {
    const activeStep = def.steps.find((s) => started.has(s) && !finished.has(s))
    const doneSteps = def.steps.filter((s) => finished.has(s))

    // Skills paralelos da persona (filtra os relevantes)
    const personaSkills = def.personaId === 'dochie'
      ? ['tech_reviewer', 'risk_reviewer', 'innovation_reviewer', 'sustainability_reviewer']
      : def.personaId === 'vangogh'
        ? ['diagrams', 'prototype']
        : []
    const activeSkills = personaSkills.filter((sid) => skillsStarted.has(sid) && !skillsFinished.has(sid))

    if (activeStep) {
      labels[def.personaId] = {
        step: activeStep,
        label: def.activeLabel(activeStep),
        activeSkills: activeSkills.length > 0 ? activeSkills : undefined,
      }
    } else if (doneSteps.length > 0) {
      labels[def.personaId] = { step: doneSteps[doneSteps.length - 1], label: def.doneLabel }
    } else {
      labels[def.personaId] = { step: '', label: def.idleLabel }
    }
  }
  return labels
}

const STEP_DEFS: Array<{ id: string; label: string; description: string }> = [
  { id: 'archie.ingest', label: 'L1', description: 'Ingestão de contexto' },
  { id: 'dochie.review', label: 'L2', description: 'Análise multidimensional (4 revisores)' },
  { id: 'archie.synthesis', label: 'L3', description: 'Síntese arquitetural (SDD + HITL)' },
  { id: 'output.parallel', label: 'L4', description: 'Artefatos: Event Storm + Diagramas + Protótipo' },
]

function deriveProgress(
  started: Set<string>,
  finished: Set<string>,
  pipelineBusy: boolean,
  hasError: boolean,
): AgentProgress {
  let activeStepIndex = -1
  const steps: AgentStepProgress[] = STEP_DEFS.map((def, idx) => {
    let state: StepState
    if (finished.has(def.id)) {
      state = 'done'
    } else if (started.has(def.id)) {
      state = hasError ? 'error' : 'active'
    } else if (pipelineBusy && activeStepIndex === -1) {
      state = hasError ? 'error' : 'active'
    } else {
      state = 'pending'
    }
    if ((state === 'active' || state === 'error') && activeStepIndex === -1) {
      activeStepIndex = idx
    }
    return { ...def, state }
  })
  const doneCount = steps.filter((s) => s.state === 'done').length
  return { steps, activeStepIndex, doneCount, total: steps.length }
}

/* ---------- hook ---------- */

export function useAgentRunState(
  events: AgentEvent[],
  pipelineBusy: boolean,
  hasSdd: boolean,
  hasPrototype: boolean,
  hasDiagrams: boolean,
  globalStatus: string,
): AgentRunState {
  const scoped = useMemo(() => sliceEventsAfterLatestRun(events), [events])

  const { started, finished, skillsStarted, skillsFinished } = useMemo(
    () => analyzePipelineSteps(scoped),
    [scoped],
  )

  const isError = useMemo(() => globalStatus.includes('Erro'), [globalStatus])

  const statuses = useMemo(
    () =>
      deriveStatuses(
        scoped,
        pipelineBusy,
        hasSdd,
        hasPrototype,
        hasDiagrams,
        isError,
      ),
    [scoped, pipelineBusy, hasSdd, hasPrototype, hasDiagrams, isError],
  )

  const peers = useMemo(
    () => derivePeers(statuses, started, finished, skillsStarted, skillsFinished),
    [statuses, started, finished, skillsStarted, skillsFinished],
  )

  const stepLabels = useMemo(
    () => deriveStepLabels(started, finished, skillsStarted, skillsFinished),
    [started, finished, skillsStarted, skillsFinished],
  )

  const progress = useMemo(
    () => deriveProgress(started, finished, pipelineBusy, isError),
    [started, finished, pipelineBusy, isError],
  )

  const activeStep = useMemo(() => findActiveStep(events), [events])

  // Eventos enriquecidos para raciocínio — preserva o array original,
  // o painel de raciocínio faz seu próprio processamento.
  const reasoningEvents = events

  const hasRunMarker = useMemo(
    () => scoped.some((e) => effectiveEventKind(e ?? {}) === 'RUN_STARTED'),
    [scoped],
  )

  const isWaitingWarmup = useMemo(() => {
    const tail = scoped[scoped.length - 1]
    return (
      pipelineBusy &&
      scoped.length > 0 &&
      effectiveEventKind(tail ?? {}) === 'RUN_FINISHED'
    )
  }, [scoped, pipelineBusy])

  return {
    statuses,
    peers,
    stepLabels,
    progress,
    reasoningEvents,
    activeStep,
    hasRunMarker,
    isWaitingWarmup,
  }
}

export { OUTPUT_SUB_STEPS }
